import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useReducer,
} from 'react';

import { VariableAction } from '@/features/variable/store/actions/variable/action-creators';
import variableReducer, {
  initialVariableState,
  VariableState,
} from '@/features/variable/store/reducers/variable-reducer';

type VariableDispatch = Dispatch<VariableAction>;

const defaultDispatch: VariableDispatch = () => {
  throw new Error('VariableDispatch was called outside of VariableProvider');
};

export const VariableContext =
  createContext<VariableState>(initialVariableState);
export const VariableDispatchContext =
  createContext<VariableDispatch>(defaultDispatch);

export const useVariableContext = () => {
  const context = useContext(VariableContext);
  if (context === undefined) {
    throw new Error(
      'useVariableContext must be used within a VariableProvider. Make sure the component is wrapped in VariableProvider.',
    );
  }
  return context;
};

export const useVariableDispatchContext = () => {
  const dispatch = useContext(VariableDispatchContext);
  if (dispatch === defaultDispatch) {
    throw new Error(
      'useVariableDispatchContext must be used within a VariableProvider. Make sure the component is wrapped in VariableProvider.',
    );
  }
  return dispatch;
};

export const VariableProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(variableReducer, initialVariableState);

  return (
    <VariableContext.Provider value={state}>
      <VariableDispatchContext.Provider value={dispatch}>
        {children}
      </VariableDispatchContext.Provider>
    </VariableContext.Provider>
  );
};

export default VariableProvider;
